import { Mail, Phone, MapPin, CheckCircle2, ListChecks, AlertTriangle } from "lucide-react";
import PageHero from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const STEPS = [
  {
    title: "Initial enquiry",
    copy: "Confirm age, gender, legal status, presenting needs and required timescale.",
  },
  {
    title: "Information review",
    copy: "Consider the court order, care and placement plans, risk assessments, education, health and safeguarding information.",
  },
  {
    title: "Multi-disciplinary matching",
    copy: "Assess staffing, environment, professional resources and compatibility with the Statement of Purpose.",
  },
  {
    title: "Child-centred transition",
    copy: "Where time permits, share accessible information, hear the child's views and agree visits or a transition plan.",
  },
  {
    title: "Decision and mobilisation",
    copy: "Record the rationale, agree funding and staffing, and complete plans before admission.",
  },
];

const CONSIDER = [
  "One boy aged 12-15 with EBD",
  "A current child-specific court order",
  "Planned or emergency referral",
  "Needs supportable within 3:1 or risk-led 4:1 staffing",
];

const CONDITIONS = [
  "Lawful, least-restrictive arrangements",
  "Safe environmental and staffing match",
  "Education, health and therapy resources available",
  "Clear funding, plans and multi-agency responsibilities",
];

export default function Referrals() {
  return (
    <div data-testid="page-referrals">
      <PageHero
        testid="referrals-hero"
        eyebrow="Working with placing authorities"
        title={[
          "Referral, matching",
          <>
            and <em className="text-rainbow italic">contact</em>
          </>,
        ]}
        lead="Referrals are considered carefully and transparently. The central question is whether Infinity & Beyond Care can meet the child's needs safely, lawfully and consistently within a single-occupancy, non-secure home."
      />

      {/* Process */}
      <section className="container-x" data-testid="process-section">
        <FadeUp>
          <p className="eyebrow">The process</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="process-title">
            Five steps from enquiry to admission
          </h2>
        </FadeUp>
        <StaggerGroup className="relative mt-12 space-y-0">
          {STEPS.map(({ title, copy }, i) => (
            <StaggerItem key={title}>
              <div className="relative flex gap-6 pb-10 last:pb-0 sm:gap-10" data-testid={`step-${i + 1}`}>
                {i < STEPS.length - 1 && (
                  <span aria-hidden="true" className="absolute left-[27px] top-16 h-[calc(100%-4rem)] w-px bg-line sm:left-[31px]" />
                )}
                <span
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-line bg-white font-display text-xl text-ink"
                  data-testid={`step-number-${i + 1}`}
                >
                  {i + 1}
                </span>
                <div className="pt-1.5">
                  <h3 className="font-display text-2xl text-ink">{title}</h3>
                  <p className="mt-2 max-w-2xl text-base leading-relaxed text-mute">{copy}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Admission parameters */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="parameters-section">
        <FadeUp>
          <p className="eyebrow">Admission parameters</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="parameters-title">
            What we can consider, and on what conditions
          </h2>
        </FadeUp>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <FadeUp>
            <div className="card h-full p-8 sm:p-10" data-testid="consider-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-600">
                <CheckCircle2 className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-display text-2xl text-ink" data-testid="consider-title">We can consider</h3>
              <ul className="mt-5 space-y-4">
                {CONSIDER.map((item, i) => (
                  <li key={item} className="flex items-start gap-3 text-base font-bold text-ink" data-testid={`consider-item-${i + 1}`}>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-teal-500" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="card h-full p-8 sm:p-10" data-testid="conditions-card">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sky/10 text-sky">
                <ListChecks className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <h3 className="mt-6 font-display text-2xl text-ink" data-testid="conditions-title">Decision conditions</h3>
              <ul className="mt-5 space-y-4">
                {CONDITIONS.map((item, i) => (
                  <li key={item} className="flex items-start gap-3 text-base font-bold text-ink" data-testid={`condition-item-${i + 1}`}>
                    <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
        <FadeUp>
          <div className="mt-8 flex items-start gap-4 rounded-3xl bg-sand p-7 sm:p-8" data-testid="decline-note">
            <AlertTriangle className="mt-1 h-5 w-5 shrink-0 text-ink" strokeWidth={1.5} />
            <p className="text-base leading-relaxed text-ink/90">
              The home will decline a referral where the child's needs fall outside the Statement of
              Purpose, required restrictions cannot be implemented lawfully and safely, or
              sufficient staffing, educational, health, therapeutic or professional resources cannot
              be secured.
            </p>
          </div>
        </FadeUp>
      </section>

      {/* Contact */}
      <section className="container-x" data-testid="contact-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-ink px-7 py-14 sm:px-14 sm:py-20">
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1.5 bg-rainbow" />
            <p className="eyebrow !text-white/60">Referrals &amp; enquiries</p>
            <h2 className="mt-5 max-w-2xl font-display text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="contact-title">
              Talk to us about a referral
            </h2>
            <div className="mt-12 grid gap-8 sm:grid-cols-3">
              <a
                href="mailto:Info@infinityandbeyonccare.co.uk"
                data-testid="contact-email"
                className="group rounded-3xl border border-white/15 p-7 transition-[transform,background-color] duration-200 hover:-translate-y-1 hover:bg-white/5"
              >
                <Mail className="h-6 w-6 text-sun" strokeWidth={1.5} />
                <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.18em] text-white/50">Email</p>
                <p className="mt-2 break-words text-base font-bold text-white">Info@infinityandbeyonccare.co.uk</p>
              </a>
              <a
                href="tel:+447748433226"
                data-testid="contact-phone"
                className="group rounded-3xl border border-white/15 p-7 transition-[transform,background-color] duration-200 hover:-translate-y-1 hover:bg-white/5"
              >
                <Phone className="h-6 w-6 text-sun" strokeWidth={1.5} />
                <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.18em] text-white/50">Phone</p>
                <p className="mt-2 text-base font-bold text-white">+44 7748 433226</p>
              </a>
              <div
                data-testid="contact-address"
                className="rounded-3xl border border-white/15 p-7"
              >
                <MapPin className="h-6 w-6 text-sun" strokeWidth={1.5} />
                <p className="mt-5 text-xs font-extrabold uppercase tracking-[0.18em] text-white/50">The home</p>
                <p className="mt-2 text-base font-bold leading-snug text-white">
                  81 Bicester Road
                  <br />
                  Aylesbury HP19 9AN
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Regulatory note */}
      <section className="container-x py-14" data-testid="regulatory-section">
        <FadeUp>
          <p className="mx-auto max-w-4xl text-sm leading-relaxed text-mute" data-testid="regulatory-copy">
            Prepared for application-stage use with regard to the Children's Homes (England)
            Regulations 2015, the Quality Standards and Ofsted registration guidance. This brochure
            should be read alongside the Statement of Purpose and child-specific plans. Registration
            and placement decisions remain subject to the relevant statutory processes.
          </p>
        </FadeUp>
      </section>
    </div>
  );
}
